/* ==========================================================================
   TRONK theme — vanilla ES6, no dependencies (Lenis optional, bundled).
   ========================================================================== */
(() => {
  'use strict';

  const T = window.TRONK || {};
  const root = document.documentElement;
  const body = document.body;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const motionOn = T.animations && !reduceMotion;
  if (!motionOn) body.classList.remove('anim-on');

  /* ---------- Helpers ---------- */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const debounce = (fn, wait = 250) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  };
  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

  const formatMoney = (cents, format = T.moneyFormat || '₹{{amount}}') => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = Number(cents) || 0;
    const fmt = (n, decimals, thousands = ',', dec = '.') => {
      const fixed = (n / 100).toFixed(decimals);
      const [int, frac] = fixed.split('.');
      const withSep = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? withSep + dec + frac : withSep;
    };
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return String(cents / 100);
    let value;
    switch (match[1]) {
      case 'amount_no_decimals': value = fmt(cents, 0); break;
      case 'amount_with_comma_separator': value = fmt(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = fmt(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = fmt(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': value = fmt(cents, 0, ' '); break;
      case 'amount_with_space_separator': value = fmt(cents, 2, ' ', ','); break;
      default: value = fmt(cents, 2);
    }
    return format.replace(match[0], value);
  };
  T.formatMoney = formatMoney;

  const toast = (msg) => {
    const el = $('[data-toast]');
    if (!el) return;
    el.textContent = msg;
    el.classList.add('is-visible');
    clearTimeout(el._t);
    el._t = setTimeout(() => el.classList.remove('is-visible'), 2600);
  };

  /* ---------- Smooth scroll (Lenis) ---------- */
  let lenis = null;
  const initLenis = () => {
    if (!T.smoothScroll || reduceMotion || typeof window.Lenis !== 'function' || window.Shopify && window.Shopify.designMode) return;
    lenis = new window.Lenis({ autoRaf: true, lerp: 0.1, smoothWheel: true, anchors: true });
    window.lenis = lenis;
  };
  const lockScroll = (lock) => {
    if (lenis) lock ? lenis.stop() : lenis.start();
    root.style.overflow = lock ? 'hidden' : '';
  };
  const scrollToY = (y) => {
    if (lenis) lenis.scrollTo(y, { duration: 1.2 });
    else window.scrollTo({ top: y, behavior: reduceMotion ? 'auto' : 'smooth' });
  };

  /* ---------- Scroll-driven state (header, back-to-top, hero, curtain) ---------- */
  const siteTop = $('[data-site-top]');
  const toTop = $('[data-to-top]');
  const onScroll = () => {
    const y = window.scrollY;
    body.classList.toggle('is-scrolled', y > 50);
    if (toTop) toTop.classList.toggle('is-visible', y > window.innerHeight * 0.9);
  };
  const setHeaderVars = () => {
    const announce = $('[data-announce]');
    const h = announce ? announce.offsetHeight : 0;
    root.style.setProperty('--announce-h', body.classList.contains('is-scrolled') ? '0px' : h + 'px');
  };

  /* Footer curtain: fixed behind the page, page gets bottom margin to reveal it */
  const initCurtain = () => {
    const curtain = $('[data-footer-curtain]');
    const wrap = $('#PageWrap');
    if (!curtain || !wrap) return;
    const footer = $('[data-curtain]', curtain);
    const enabled = footer && footer.dataset.curtain === 'true';
    const update = () => {
      const h = curtain.offsetHeight;
      const ok = enabled && window.innerWidth >= 750 && h < window.innerHeight * 0.95;
      curtain.classList.toggle('is-fixed', ok);
      wrap.style.marginBottom = ok ? h + 'px' : '';
    };
    update();
    if ('ResizeObserver' in window) new ResizeObserver(update).observe(curtain);
    window.addEventListener('resize', debounce(update, 150));
  };

  /* ---------- Reveal + accents on view ---------- */
  const io = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (!e.isIntersecting) return;
      e.target.classList.add(e.target.hasAttribute('data-accent') ? 'is-drawn' : 'is-in');
      io.unobserve(e.target);
    });
  }, { rootMargin: '0px 0px -10% 0px', threshold: 0.1 }) : null;
  const observeReveals = (ctx = document) => {
    const els = $$('.reveal, [data-accent]', ctx);
    if (!io || !motionOn) { els.forEach((el) => el.classList.add('is-in', 'is-drawn')); return; }
    els.forEach((el) => io.observe(el));
  };

  /* In-view helper for continuous motion */
  const whenVisible = (el, onIn, onOut) => {
    if (!('IntersectionObserver' in window)) { onIn(); return; }
    new IntersectionObserver(([e]) => (e.isIntersecting ? onIn() : onOut && onOut()), { threshold: 0.05 }).observe(el);
  };

  /* ---------- Model motion: shared scroll tick (driven by Lenis when active, otherwise the existing scroll handler) ---------- */
  const M = T.motion || {};
  const motionTick = new Set();
  let tickQueued = false;
  const runMotionTick = () => {
    if (tickQueued || !motionTick.size) return;
    tickQueued = true;
    requestAnimationFrame(() => { tickQueued = false; motionTick.forEach((fn) => fn()); });
  };

  /* ---------- Focus trap for dialogs ---------- */
  const focusables = (el) => $$('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])', el).filter((n) => n.offsetParent !== null);

  class Dialog extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => { if (e.target.closest('[data-close]')) this.close(); });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
        if (e.key === 'Tab') {
          const f = focusables(this);
          if (!f.length) return;
          const first = f[0], last = f[f.length - 1];
          if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
          else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
      });
    }
    open(opener) {
      this.opener = opener || document.activeElement;
      this.hidden = false;
      requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-open')));
      lockScroll(true);
      $$(`[aria-controls="${this.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
      setTimeout(() => { const f = this.querySelector('[data-search-input]') || focusables(this)[0]; if (f) f.focus(); }, 60);
    }
    close() {
      if (this.hidden) return;
      this.classList.remove('is-open');
      $$(`[aria-controls="${this.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
      setTimeout(() => { this.hidden = true; }, 420);
      lockScroll(false);
      if (this.opener && this.opener.focus) this.opener.focus();
    }
  }
  customElements.define('tronk-drawer', class extends Dialog {});
  customElements.define('tronk-modal', class extends Dialog {});
  const openDialog = (id, opener) => { const d = document.getElementById(id); if (d && d.open) d.open(opener); };
  const closeAll = () => $$('tronk-drawer, tronk-modal').forEach((d) => d.close());

  /* ---------- Cart ---------- */
  const Cart = {
    sectionsParam() { return $('#CartDrawer') ? 'cart-drawer' : ''; },
    renderDrawer(sections) {
      if (!sections || !sections['cart-drawer']) return;
      const html = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      const fresh = html.querySelector('[data-cart-drawer-inner]');
      const current = $('#CartDrawer [data-cart-drawer-inner]');
      if (fresh && current) current.innerHTML = fresh.innerHTML;
    },
    updateCount(count) { $$('[data-cart-count]').forEach((el) => { el.textContent = count; }); },
    async refreshCount() {
      try { const c = await (await fetch(`${T.routes.cart}.js`)).json(); this.updateCount(c.item_count); return c; } catch (e) { return null; }
    },
    async add(items, opener) {
      const payload = { items };
      const sections = this.sectionsParam();
      if (sections) { payload.sections = sections; payload.sections_url = window.location.pathname; }
      const res = await fetch(`${T.routes.cartAdd}.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || T.strings.error);
      this.renderDrawer(data.sections);
      await this.refreshCount();
      closeAll();
      if (T.cartType === 'drawer' && $('#CartDrawer')) setTimeout(() => openDialog('CartDrawer', opener), 60);
      else window.location.href = T.routes.cart;
      return data;
    },
    async change(line, quantity) {
      const drawer = $('#CartDrawer [data-cart-drawer-inner]');
      if (drawer) drawer.classList.add('is-updating');
      try {
        const res = await fetch(`${T.routes.cartChange}.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity, sections: this.sectionsParam(), sections_url: window.location.pathname })
        });
        const data = await res.json();
        if (data.status) throw new Error(data.description || data.message);
        this.renderDrawer(data.sections);
        this.updateCount(data.item_count);
      } catch (e) { toast(e.message || T.strings.error); }
      finally { const d = $('#CartDrawer [data-cart-drawer-inner]'); if (d) d.classList.remove('is-updating'); }
    }
  };
  T.cart = Cart;

  /* ---------- Product form (variant picker + add) ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      if (this._init) return; this._init = true;
      this.variants = JSON.parse((this.querySelector('[data-variants]') || {}).textContent || '[]');
      try { this.inventory = JSON.parse((this.querySelector('[data-inventory]') || {}).textContent || '{}'); } catch (e) { this.inventory = {}; }
      this.form = this.querySelector('form');
      this.idInput = this.querySelector('[data-variant-id]');
      this.button = this.querySelector('[data-add]');
      this.label = this.querySelector('[data-add-label]');
      this.stock = this.querySelector('[data-stock]');
      this.error = this.querySelector('[data-error]');
      this.root = this.closest('[data-product-root]') || this;
      this.addEventListener('change', (e) => { if (e.target.matches('.opt__input')) this.onChange(); });
      if (this.form) this.form.addEventListener('submit', (e) => this.onSubmit(e));
      this.update(this.currentVariant());
    }
    selectedOptions() {
      return $$('.opt', this).map((fs) => { const c = fs.querySelector('.opt__input:checked'); return c ? c.value : null; });
    }
    currentVariant() {
      const id = Number(this.idInput && this.idInput.value);
      return this.variants.find((v) => v.id === id) || this.variants[0];
    }
    onChange() {
      const opts = this.selectedOptions();
      $$('.opt', this).forEach((fs, i) => { const out = fs.querySelector('[data-option-value]'); if (out) out.textContent = opts[i] || ''; });
      const variant = this.variants.find((v) => v.options.every((o, i) => o === opts[i]));
      this.update(variant);
      if (variant && this.hasAttribute('data-update-url')) {
        const url = new URL(window.location.href); url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }
    update(variant) {
      if (!this.button) return;
      if (!variant) {
        this.button.disabled = true; this.label.textContent = T.strings.unavailable;
        if (this.stock) this.stock.textContent = '';
        return;
      }
      this.idInput.value = variant.id;
      this.button.disabled = !variant.available;
      this.label.textContent = variant.available ? T.strings.addToCart : T.strings.soldOut;
      const now = this.root.querySelector('[data-price-now]');
      const was = this.root.querySelector('[data-price-was]');
      const priceWrap = this.root.querySelector('[data-price]');
      if (now) now.textContent = formatMoney(variant.price);
      if (was) {
        const sale = variant.compare_at_price && variant.compare_at_price > variant.price;
        was.hidden = !sale; was.textContent = sale ? formatMoney(variant.compare_at_price) : '';
        if (priceWrap) priceWrap.classList.toggle('price--sale', !!sale);
      }
      if (this.stock) {
        const inv = this.inventory[variant.id];
        this.stock.classList.remove('is-low');
        if (!variant.available) this.stock.textContent = '';
        else if (inv && inv.m === 'shopify' && inv.p === 'deny' && inv.q > 0 && inv.q <= 5) {
          this.stock.textContent = (T.strings.lowStock || '').replace('__COUNT__', inv.q); this.stock.classList.add('is-low');
        } else this.stock.textContent = T.strings.inStock;
      }
      // mark unavailable combos for the last option (usually size)
      const opts = this.selectedOptions();
      $$('.opt', this).forEach((fs, idx) => {
        $$('.opt__input', fs).forEach((input) => {
          const test = opts.slice(); test[idx] = input.value;
          const match = this.variants.find((v) => v.options.every((o, i) => o === test[i]));
          input.classList.toggle('is-unavailable', !match || !match.available);
        });
      });
    }
    async onSubmit(e) {
      e.preventDefault();
      if (!this.button || this.button.disabled) return;
      if (this.error) this.error.hidden = true;
      const prev = this.label.textContent;
      this.button.setAttribute('aria-busy', 'true'); this.label.textContent = T.strings.adding;
      try {
        await Cart.add([{ id: Number(this.idInput.value), quantity: 1 }], this.button);
        this.label.textContent = prev;
      } catch (err) {
        this.label.textContent = prev;
        if (this.error) { this.error.textContent = err.message; this.error.hidden = false; }
      } finally { this.button.removeAttribute('aria-busy'); }
    }
  }
  customElements.define('product-form', ProductForm);

  /* ---------- Quick view ---------- */
  const quickView = async (handle, fallbackUrl, opener) => {
    const modal = $('#QuickView');
    const content = modal && $('[data-modal-content]', modal);
    if (!content) { window.location.href = fallbackUrl; return; }
    content.innerHTML = '<div class="spinner" role="status" aria-label="Loading"></div>';
    content.classList.add('is-loading');
    modal.open(opener);
    try {
      const res = await fetch(`${T.routes.root.replace(/\/$/, '')}/products/${encodeURIComponent(handle)}?section_id=quick-view`);
      if (!res.ok) throw new Error('not found');
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const qv = doc.querySelector('.qv');
      if (!qv) throw new Error('empty');
      content.classList.remove('is-loading');
      content.innerHTML = '';
      content.appendChild(qv);
      const f = $('button, a', content); if (f) f.focus();
    } catch (e) {
      modal.close();
      if (fallbackUrl) window.location.href = fallbackUrl;
    }
  };

  /* ---------- Global click delegation ---------- */
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-open]');
    if (opener) { e.preventDefault(); openDialog(opener.dataset.open, opener); return; }

    const cartToggle = e.target.closest('[data-cart-toggle]');
    if (cartToggle && T.cartType === 'drawer' && $('#CartDrawer')) { e.preventDefault(); openDialog('CartDrawer', cartToggle); return; }

    const qv = e.target.closest('[data-quick-view]');
    if (qv) { e.preventDefault(); quickView(qv.dataset.quickView, qv.dataset.url, qv); return; }

    const qty = e.target.closest('[data-qty-change]');
    if (qty) { e.preventDefault(); Cart.change(Number(qty.dataset.lineIndex), Number(qty.dataset.qtyChange)); return; }

    const addAll = e.target.closest('[data-add-all]');
    if (addAll) {
      e.preventDefault();
      const ids = (addAll.dataset.addAll || '').split(',').map(Number).filter(Boolean);
      if (!ids.length) { toast(T.strings.unavailable); return; }
      addAll.setAttribute('aria-busy', 'true');
      Cart.add(ids.map((id) => ({ id, quantity: 1 })), addAll).catch((err) => toast(err.message)).finally(() => addAll.removeAttribute('aria-busy'));
      return;
    }

    if (e.target.closest('[data-to-top]')) { scrollToY(0); return; }

    const share = e.target.closest('[data-share]');
    if (share) {
      e.preventDefault();
      const url = share.dataset.share || window.location.href;
      if (navigator.share) navigator.share({ url, title: document.title }).catch(() => {});
      else if (navigator.clipboard) navigator.clipboard.writeText(url).then(() => toast(T.strings.copied));
      return;
    }

    // Offers popover
    const offers = e.target.closest('[data-offers-toggle]');
    const panel = $('#OffersPanel');
    if (offers && panel) {
      const open = panel.hidden; panel.hidden = !open; offers.setAttribute('aria-expanded', String(open)); return;
    }
    if (panel && !panel.hidden && !e.target.closest('#OffersPanel')) { panel.hidden = true; const b = $('[data-offers-toggle]'); if (b) b.setAttribute('aria-expanded', 'false'); }

    // Mega menu (click/keyboard)
    const mega = e.target.closest('[data-mega-toggle]');
    $$('[data-mega-toggle][aria-expanded="true"]').forEach((b) => { if (b !== mega) b.setAttribute('aria-expanded', 'false'); });
    if (mega) { mega.setAttribute('aria-expanded', String(mega.getAttribute('aria-expanded') !== 'true')); }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    $$('[data-mega-toggle][aria-expanded="true"]').forEach((b) => { b.setAttribute('aria-expanded', 'false'); b.focus(); });
    const panel = $('#OffersPanel'); if (panel) panel.hidden = true;
  });

  /* ---------- Localization selector ---------- */
  customElements.define('loc-select', class extends HTMLElement {
    connectedCallback() {
      this.btn = this.querySelector('.loc__btn'); this.list = this.querySelector('.loc__list');
      this.btn.addEventListener('click', () => this.toggle());
      this.list.addEventListener('click', (e) => {
        const opt = e.target.closest('.loc__opt'); if (!opt) return;
        this.querySelector('input[type="hidden"]').value = opt.dataset.value;
        this.closest('form').submit();
      });
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.toggle(false); });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') { this.toggle(false); this.btn.focus(); } });
    }
    toggle(force) {
      const open = typeof force === 'boolean' ? force : this.list.hidden;
      this.list.hidden = !open; this.btn.setAttribute('aria-expanded', String(open));
    }
  });

  /* ---------- Predictive search ---------- */
  customElements.define('predictive-search', class extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-search-input]'); this.results = this.querySelector('[data-results]');
      this.input.addEventListener('input', debounce(() => this.search(), 280));
    }
    async search() {
      const q = this.input.value.trim();
      if (!q) { this.results.innerHTML = ''; return; }
      try {
        const url = `${T.routes.predictive}?q=${encodeURIComponent(q)}&resources[type]=product,collection,article,page&resources[limit]=6&resources[options][fields]=title,product_type,variants.title,vendor,tag&section_id=predictive-search`;
        const html = await (await fetch(url)).text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const section = doc.querySelector('.shopify-section') || doc.body;
        this.results.innerHTML = section.innerHTML;
      } catch (e) { this.results.innerHTML = `<p>${T.strings.noResults}</p>`; }
    }
  });

  /* ---------- Accordions with smooth height ---------- */
  const initAccordions = (ctx = document) => {
    $$('details.acc', ctx).forEach((d) => {
      if (d._acc) return; d._acc = true;
      const summary = d.querySelector('summary'); const content = d.querySelector('.acc__body');
      if (!summary || !content) return;
      summary.addEventListener('click', (e) => {
        if (!motionOn) return;
        e.preventDefault();
        if (d.open) {
          const h = content.scrollHeight;
          content.animate([{ height: h + 'px' }, { height: '0px' }], { duration: 300, easing: 'ease' }).onfinish = () => { d.open = false; };
        } else {
          d.open = true;
          const h = content.scrollHeight;
          content.animate([{ height: '0px' }, { height: h + 'px' }], { duration: 320, easing: 'ease' });
        }
      });
    });
  };

  /* ---------- Hero: pinned, blur + darken as the next section overlaps ---------- */
  customElements.define('hero-pin', class extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section');
      if (this.section) this.section.classList.add('is-pinned-hero');
      this.syncHeaderOffset();
      if (!motionOn) return;
      const tick = () => {
        const h = this.offsetHeight || window.innerHeight;
        const p = clamp(window.scrollY / h, 0, 1);
        this.style.setProperty('--p', p.toFixed(3));
        this.classList.toggle('is-covered', window.scrollY > h * 1.1);
      };
      window.addEventListener('scroll', tick, { passive: true });
      tick();
    }
    /* Offset the hero photos by the real height of the fixed header (announcement + header bar) at rest,
       so faces are never hidden behind it. Only measured while the header is in its top-of-page state. */
    syncHeaderOffset() {
      const top = $('[data-site-top]');
      if (!top) return;
      const measure = () => {
        if (body.classList.contains('is-scrolled')) return;
        const h = Math.round(top.getBoundingClientRect().height);
        if (h > 0) this.style.setProperty('--hero-offset', h + 'px');
      };
      const later = debounce(measure, 350);
      measure();
      if ('ResizeObserver' in window) new ResizeObserver(later).observe(top);
      window.addEventListener('resize', later);
      window.addEventListener('load', measure);
    }
  });

  /* ---------- Countdown ---------- */
  customElements.define('count-down', class extends HTMLElement {
    connectedCallback() {
      const raw = (this.dataset.end || '').trim();
      this.end = new Date(raw.length <= 10 ? raw + 'T23:59:59' : raw).getTime();
      this.parts = { d: this.querySelector('[data-d]'), h: this.querySelector('[data-h]'), m: this.querySelector('[data-m]'), s: this.querySelector('[data-s]') };
      this.tick(); this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    tick() {
      let diff = Math.max(0, (this.end || 0) - Date.now());
      if (isNaN(diff)) diff = 0;
      const d = Math.floor(diff / 864e5), h = Math.floor(diff / 36e5) % 24, m = Math.floor(diff / 6e4) % 60, s = Math.floor(diff / 1e3) % 60;
      const pad = (n) => String(n).padStart(2, '0');
      if (this.parts.d) this.parts.d.textContent = pad(d);
      if (this.parts.h) this.parts.h.textContent = pad(h);
      if (this.parts.m) this.parts.m.textContent = pad(m);
      if (this.parts.s) this.parts.s.textContent = pad(s);
      if (diff === 0) { clearInterval(this.timer); this.classList.add('is-ended'); }
    }
  });

  /* ---------- Cycling media: turntable (product cards), crossfade, wipe ---------- */
  class Cycler extends HTMLElement {
    get interval() { return 3500; }
    get selector() { return 'img, .placeholder-svg'; }
    connectedCallback() {
      this.items = $$(this.selector, this).filter((el) => el.parentElement === this || el.closest(this.tagName.toLowerCase()) === this);
      if (this.items.length < 2 || !motionOn || !this.enabled()) return;
      this.i = 0;
      whenVisible(this, () => this.start(), () => this.stop());
    }
    enabled() { return true; }
    start() {
      if (this.timer) return;
      this.warm((this.i + 1) % this.items.length);
      this.timer = setInterval(() => this.step(), this.interval);
    }
    stop() { clearInterval(this.timer); this.timer = null; }
    restart() { if (!this.timer) return; this.stop(); this.start(); }
    warm(i) { const el = this.items[i]; if (el && el.loading === 'lazy') el.loading = 'eager'; }
    step() {
      if (this.busy) return;
      const prev = this.items[this.i];
      const nextI = (this.i + 1) % this.items.length;
      const next = this.items[nextI];
      const go = () => {
        this.busy = false;
        this.i = nextI;
        this.show(prev, next);
        this.warm((nextI + 1) % this.items.length);
      };
      if (next.tagName === 'IMG' && !(next.complete && next.naturalWidth)) {
        this.busy = true;
        next.loading = 'eager';
        (next.decode ? next.decode() : Promise.reject()).then(go, () => { if (next.complete) go(); else this.busy = false; });
      } else go();
    }
    show(prev, next) { prev.classList.remove('is-active'); next.classList.add('is-active'); }
    disconnectedCallback() { this.stop(); }
  }
  customElements.define('turn-table', class extends Cycler {
    get interval() { return this.fast ? 600 : (T.turntableMs || 1200); }
    enabled() { return T.turntable !== false; }
    connectedCallback() {
      super.connectedCallback();
      if (this.i === undefined) return; // not cycling (one image, disabled, or reduced motion)
      const card = this.closest('.card');
      if (!card) return;
      card.addEventListener('mouseenter', () => { this.fast = true; this.restart(); });
      card.addEventListener('mouseleave', () => { this.fast = false; this.restart(); });
    }
    show(prev, next) {
      if (M.turntable === 'instant') { super.show(prev, next); return; }
      this.items.forEach((el) => { if (el !== prev) el.classList.remove('is-prev'); });
      prev.classList.remove('is-active'); prev.classList.add('is-prev');
      next.classList.add('is-active');
      clearTimeout(this.prevTimer);
      this.prevTimer = setTimeout(() => prev.classList.remove('is-prev'), 420);
    }
  });
  customElements.define('cross-fade', class extends Cycler {
    get interval() { return Number(this.dataset.interval) || 3800; }
  });
  customElements.define('wipe-cycle', class extends Cycler {
    get interval() { return Number(this.dataset.interval) || 4200; }
    show(prev, next) {
      this.items.forEach((el) => el.classList.remove('is-prev'));
      prev.classList.remove('is-active'); prev.classList.add('is-prev');
      next.classList.add('is-active');
    }
  });

  /* ---------- Model motion: shared video controller ---------- */
  const saveData = !!(navigator.connection && navigator.connection.saveData);
  const smallScreen = window.matchMedia('(max-width: 749px)');
  const VideoHub = {
    all: new Set(),
    inView: new Set(),
    queued: false,
    schedule() {
      if (this.queued) return;
      this.queued = true;
      requestAnimationFrame(() => { this.queued = false; this.update(); });
    },
    update() {
      const allow = new Set();
      if (!document.hidden) {
        const mid = window.innerHeight / 2;
        const ranked = Array.from(this.inView)
          .filter((el) => el.autoplay && !el.follower && !el.closest('.is-covered')) // e.g. pinned hero once covered
          .map((el) => { const r = el.getBoundingClientRect(); return { el, d: Math.abs(r.top + r.height / 2 - mid) }; })
          .sort((a, b) => a.d - b.d);
        if (saveData || smallScreen.matches) {
          if (ranked[0]) allow.add(ranked[0].el); // one at a time on mobile / Save-Data
        } else {
          let cards = 0;
          ranked.forEach(({ el }) => {
            if (el.kind === 'card') { if (cards < 4) { allow.add(el); cards += 1; } } else allow.add(el);
          });
        }
      }
      this.all.forEach((el) => {
        if (el.follower) return;
        if (allow.has(el) || (el.hovering && !document.hidden)) el.play(); else el.pause();
      });
    }
  };
  document.addEventListener('visibilitychange', () => VideoHub.update());
  motionTick.add(() => { if (VideoHub.inView.size) VideoHub.update(); });

  customElements.define('motion-video', class extends HTMLElement {
    connectedCallback() {
      // Reduced motion / animations off: the image underneath stays as the poster, nothing loads.
      if (!motionOn || this.ready) return;
      this.ready = true;
      this.kind = this.hasAttribute('data-card') ? 'card' : 'section';
      this.autoplay = !(this.kind === 'card' && M.cardVideo === 'hover');
      this.follower = !!this.dataset.syncFollow;
      VideoHub.all.add(this);
      if ('IntersectionObserver' in window) {
        this.nearIO = new IntersectionObserver(([e]) => { if (e.isIntersecting) { this.load(); this.nearIO.disconnect(); } }, { rootMargin: '200px' });
        this.nearIO.observe(this);
        this.viewIO = new IntersectionObserver(([e]) => {
          if (e.isIntersecting) VideoHub.inView.add(this); else VideoHub.inView.delete(this);
          VideoHub.schedule();
        }, { threshold: this.kind === 'card' ? 0.4 : 0.25 });
        this.viewIO.observe(this);
      } else { this.load(); VideoHub.inView.add(this); VideoHub.schedule(); }
      if (!this.autoplay) {
        const host = this.closest('.card') || this.parentElement;
        host.addEventListener('mouseenter', () => { this.hovering = true; this.play(); });
        host.addEventListener('mouseleave', () => { this.hovering = false; this.pause(); });
      }
    }
    disconnectedCallback() {
      VideoHub.all.delete(this); VideoHub.inView.delete(this);
      if (this.nearIO) this.nearIO.disconnect();
      if (this.viewIO) this.viewIO.disconnect();
      if (this.video) this.video.pause();
    }
    load() {
      if (this.video) return this.video;
      const tpl = this.querySelector('template');
      if (!tpl) return null;
      this.appendChild(tpl.content.cloneNode(true));
      const v = this.querySelector('video');
      if (!v) return null;
      v.muted = true; v.defaultMuted = true; v.loop = true; v.playsInline = true;
      v.removeAttribute('autoplay'); v.removeAttribute('controls');
      v.setAttribute('aria-hidden', 'true'); v.tabIndex = -1;
      v.preload = (saveData || smallScreen.matches) ? 'none' : 'auto';
      v.classList.add('mv__video');
      v.addEventListener('playing', () => this.classList.add('is-playing'));
      if (this.dataset.sync) v.addEventListener('timeupdate', () => this.syncFollowers());
      this.video = v;
      return v;
    }
    followers() { return this.dataset.sync ? $$(`motion-video[data-sync-follow="${this.dataset.sync}"]`) : []; }
    syncFollowers() {
      const t = this.video.currentTime;
      this.followers().forEach((f) => { const fv = f.load(); if (fv && Math.abs(fv.currentTime - t) > 0.15) fv.currentTime = t; });
    }
    play() {
      const v = this.load();
      if (!v) return;
      if (v.paused) { const p = v.play(); if (p && p.catch) p.catch(() => {}); }
      this.followers().forEach((f) => {
        const fv = f.load();
        if (!fv) return;
        if (Math.abs(fv.currentTime - v.currentTime) > 0.15) fv.currentTime = v.currentTime;
        if (fv.paused) { const p = fv.play(); if (p && p.catch) p.catch(() => {}); }
      });
    }
    pause() {
      if (this.video && !this.video.paused) this.video.pause();
      this.followers().forEach((f) => { if (f.video && !f.video.paused) f.video.pause(); });
    }
  });

  /* ---------- Model motion: scroll parallax (0.88× scroll speed, inside overflow: hidden) ---------- */
  const pxLive = new Set();
  const pxSeen = new WeakSet();
  let pxIO = null;
  const initParallax = (ctx = document) => {
    if (!motionOn || !M.parallax || !('IntersectionObserver' in window)) return;
    if (!pxIO) {
      pxIO = new IntersectionObserver((entries) => {
        entries.forEach((e) => (e.isIntersecting ? pxLive.add(e.target) : pxLive.delete(e.target)));
        runMotionTick();
      }, { rootMargin: '120px 0px' });
      motionTick.add(() => {
        if (!pxLive.size) return;
        const mid = window.innerHeight / 2;
        pxLive.forEach((el) => {
          if (!el.isConnected) { pxLive.delete(el); return; }
          const box = el.parentElement.getBoundingClientRect();
          const max = box.height * 0.06;
          const y = clamp(-(box.top + box.height / 2 - mid) * 0.12, -max, max);
          el.style.transform = `translate3d(0, ${y.toFixed(1)}px, 0)`;
        });
      });
    }
    $$('[data-parallax]', ctx).forEach((el) => { if (!pxSeen.has(el)) { pxSeen.add(el); pxIO.observe(el); } });
  };

  /* ---------- Hotspots ---------- */
  customElements.define('hot-spot', class extends HTMLElement {
    connectedCallback() {
      this.btn = this.querySelector('.hotspot__dot, .hotspot__tag');
      this.pop = this.querySelector('.look-pop');
      $$('button.hotspot__dot, button.hotspot__tag', this).forEach((b) => b.addEventListener('click', (e) => {
        if (this.pop) {
          e.preventDefault(); e.stopPropagation();
          const open = this.pop.hidden;
          $$('hot-spot .look-pop').forEach((p) => { p.hidden = true; });
          $$('hot-spot button[aria-expanded]').forEach((x) => x.setAttribute('aria-expanded', 'false'));
          this.pop.hidden = !open; b.setAttribute('aria-expanded', String(open));
        }
      }));
    }
  });
  document.addEventListener('click', (e) => {
    if (!e.target.closest('hot-spot')) $$('hot-spot .look-pop').forEach((p) => { p.hidden = true; });
  });

  /* ---------- Carousels ---------- */
  const scrollTrack = (track, dir) => {
    const card = track.firstElementChild; if (!card) return;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 20;
    track.scrollBy({ left: dir * (card.getBoundingClientRect().width + gap), behavior: reduceMotion ? 'auto' : 'smooth' });
  };
  customElements.define('scroll-carousel', class extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        const b = e.target.closest('[data-dir]'); if (!b) return;
        const track = this.querySelector('.track:not([hidden]), [role="tabpanel"]:not([hidden]) .track') || this.querySelector('.track');
        scrollTrack(track, Number(b.dataset.dir));
      });
    }
  });
  customElements.define('tab-carousel', class extends HTMLElement {
    connectedCallback() {
      this.tabs = $$('[role="tab"]', this); this.panels = $$('[role="tabpanel"]', this);
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            e.preventDefault();
            const n = (i + (e.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
            this.select(n); this.tabs[n].focus();
          }
        });
      });
      this.addEventListener('click', (e) => {
        const b = e.target.closest('[data-dir]'); if (!b) return;
        const panel = this.panels.find((p) => !p.hidden); if (panel) scrollTrack($('.track', panel), Number(b.dataset.dir));
      });
    }
    select(i) {
      this.tabs.forEach((t, n) => { t.setAttribute('aria-selected', String(n === i)); t.tabIndex = n === i ? 0 : -1; });
      this.panels.forEach((p, n) => { p.hidden = n !== i; });
    }
  });

  /* ---------- Testimonials ---------- */
  customElements.define('testimonial-tabs', class extends HTMLElement {
    connectedCallback() {
      this.quotes = $$('[data-quote]', this); this.names = $$('[data-name]', this); this.i = 0;
      this.names.forEach((n, i) => n.addEventListener('click', () => this.go(i)));
      const next = this.querySelector('[data-next]'); if (next) next.addEventListener('click', () => this.go((this.i + 1) % this.quotes.length));
    }
    go(i) {
      this.i = i;
      this.quotes.forEach((q, n) => { q.hidden = n !== i; });
      this.names.forEach((b, n) => b.setAttribute('aria-selected', String(n === i)));
    }
  });

  /* ---------- Before / after compare ---------- */
  customElements.define('image-compare', class extends HTMLElement {
    connectedCallback() {
      const range = this.querySelector('input[type="range"]');
      const set = (v) => this.style.setProperty('--pos', v + '%');
      range.addEventListener('input', () => set(range.value));
      set(range.value);
      // allow dragging anywhere on the frame (mouse + touch)
      const frame = this.querySelector('.compare__frame');
      let dragging = false;
      const move = (x) => {
        const r = frame.getBoundingClientRect();
        const v = clamp(((x - r.left) / r.width) * 100, 0, 100);
        range.value = v; set(v);
      };
      frame.addEventListener('pointerdown', (e) => { dragging = true; frame.setPointerCapture(e.pointerId); move(e.clientX); });
      frame.addEventListener('pointermove', (e) => { if (dragging) move(e.clientX); });
      frame.addEventListener('pointerup', () => { dragging = false; });
      frame.addEventListener('pointercancel', () => { dragging = false; });
    }
  });

  /* ---------- Bundle builder ---------- */
  customElements.define('bundle-builder', class extends HTMLElement {
    connectedCallback() {
      this.discount = Number(this.dataset.discount) || 0;
      this.items = $$('[data-bundle-item]', this);
      this.addEventListener('change', () => this.update());
      const btn = this.querySelector('[data-bundle-add]');
      if (btn) btn.addEventListener('click', () => this.add(btn));
      this.update();
    }
    variantFor(item) {
      const data = JSON.parse((item.querySelector('[data-variants]') || {}).textContent || '[]');
      if (!data.length) return null;
      const opts = $$('select[data-option-index]', item).map((s) => s.value);
      return data.find((v) => v.options.every((o, i) => opts[i] === undefined || o === opts[i])) || null;
    }
    update() {
      let count = 0, total = 0;
      this.selected = [];
      this.items.forEach((item) => {
        const on = item.querySelector('[data-include]').checked;
        item.classList.toggle('is-off', !on);
        let price = Number(item.dataset.price) || 0;
        const v = this.variantFor(item);
        if (v) { price = v.price; const p = item.querySelector('[data-item-price]'); if (p) p.textContent = formatMoney(v.price); }
        if (on) { count += 1; total += price; if (v && v.available) this.selected.push(v.id); }
      });
      const saving = Math.round(total * this.discount / 100);
      const set = (sel, val) => $$(sel, this).forEach((el) => { el.textContent = val; });
      set('[data-sum-count]', count);
      set('[data-sum-original]', formatMoney(total));
      set('[data-sum-saving]', '−' + formatMoney(saving));
      set('[data-sum-total]', formatMoney(total - saving));
      const btn = this.querySelector('[data-bundle-add]');
      if (btn) btn.disabled = count === 0;
    }
    async add(btn) {
      if (!this.selected.length) { window.location.href = T.routes.allProducts; return; }
      btn.setAttribute('aria-busy', 'true');
      try { await Cart.add(this.selected.map((id) => ({ id, quantity: 1 })), btn); }
      catch (e) { toast(e.message); }
      finally { btn.removeAttribute('aria-busy'); }
    }
  });

  /* ---------- Pincode delivery estimate (UI only) ---------- */
  customElements.define('pincode-check', class extends HTMLElement {
    connectedCallback() {
      const form = this.querySelector('form'); const input = this.querySelector('input'); const out = this.querySelector('[data-out]');
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const pin = input.value.trim();
        if (!/^[1-9][0-9]{5}$/.test(pin)) { out.textContent = T.strings.invalidPin; return; }
        const metros = ['11', '40', '56', '60', '70', '50', '41', '12', '38'];
        const days = metros.includes(pin.slice(0, 2)) ? 3 : (['7', '8', '9'].includes(pin[0]) ? 7 : 5);
        const d = new Date(); d.setDate(d.getDate() + days);
        const label = d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
        out.textContent = (T.strings.deliveryBy || '').replace('__DATE__', label);
      });
    }
  });

  /* ---------- Collection: facets + grid toggle ---------- */
  customElements.define('facet-form', class extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.sectionId = this.dataset.section;
      const run = debounce(() => this.submit(), 450);
      this.form.addEventListener('input', (e) => { if (e.target.type === 'number') run(); else this.submit(); });
      this.form.addEventListener('submit', (e) => { e.preventDefault(); this.submit(); });
      document.addEventListener('click', (e) => {
        const a = e.target.closest('[data-facet-link]');
        if (a && this.contains(a) || a && a.closest(`#Section-${this.sectionId}`)) { e.preventDefault(); this.load(new URL(a.href).searchParams.toString()); }
      });
    }
    submit() {
      const fd = new FormData(this.form);
      const params = new URLSearchParams();
      for (const [k, v] of fd.entries()) if (v !== '') params.append(k, v);
      this.load(params.toString());
    }
    async load(query) {
      const grid = document.getElementById('ProductGridContainer');
      if (grid) grid.classList.add('is-updating');
      const url = `${window.location.pathname}?${query}`;
      try {
        const html = await (await fetch(`${url}${query ? '&' : ''}section_id=${this.sectionId}`)).text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        ['ProductGridContainer', 'FacetsActive', 'ProductCount'].forEach((id) => {
          const fresh = doc.getElementById(id), cur = document.getElementById(id);
          if (fresh && cur) cur.innerHTML = fresh.innerHTML;
        });
        const freshForm = doc.querySelector('facet-form form');
        if (freshForm) {
          // keep open state of dropdowns
          const open = $$('details[open]', this.form).map((d) => d.dataset.key);
          this.form.innerHTML = freshForm.innerHTML;
          open.forEach((k) => { const d = this.form.querySelector(`details[data-key="${k}"]`); if (d) d.open = true; });
        }
        window.history.replaceState({}, '', url);
        observeReveals(document.getElementById('ProductGridContainer'));
      } catch (e) { window.location.href = url; }
      finally { if (grid) grid.classList.remove('is-updating'); }
    }
  });
  customElements.define('grid-toggle', class extends HTMLElement {
    connectedCallback() {
      const grid = document.getElementById(this.dataset.target);
      const saved = (() => { try { return localStorage.getItem('tronk-grid'); } catch (e) { return null; } })();
      const apply = (n) => {
        if (grid) grid.style.setProperty('--cols', n);
        $$('button', this).forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.cols === String(n))));
      };
      if (saved) apply(saved);
      this.addEventListener('click', (e) => {
        const b = e.target.closest('button[data-cols]'); if (!b) return;
        apply(b.dataset.cols); try { localStorage.setItem('tronk-grid', b.dataset.cols); } catch (err) { /* ignore */ }
      });
    }
  });

  /* ---------- Product recommendations & recently viewed ---------- */
  customElements.define('product-recs', class extends HTMLElement {
    connectedCallback() {
      whenVisible(this, async () => {
        if (this._loaded) return; this._loaded = true;
        try {
          const html = await (await fetch(this.dataset.url)).text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const inner = doc.querySelector('product-recs');
          if (inner && inner.innerHTML.trim()) { this.innerHTML = inner.innerHTML; $$('turn-table', this).forEach(() => {}); observeReveals(this); }
          else this.closest('.shopify-section').hidden = true;
        } catch (e) { /* ignore */ }
      });
    }
  });
  customElements.define('recently-viewed', class extends HTMLElement {
    connectedCallback() {
      const key = 'tronk-recent';
      let list = [];
      try { list = JSON.parse(localStorage.getItem(key) || '[]'); } catch (e) { list = []; }
      const current = this.dataset.current;
      if (current) {
        list = [current].concat(list.filter((h) => h !== current)).slice(0, 12);
        try { localStorage.setItem(key, JSON.stringify(list)); } catch (e) { /* ignore */ }
      }
      const show = list.filter((h) => h !== current).slice(0, Number(this.dataset.limit) || 4);
      if (!show.length) { this.closest('.shopify-section').hidden = true; return; }
      this.render(show);
    }
    async render(handles) {
      const track = this.querySelector('.track');
      const base = T.routes.root.replace(/\/$/, '');
      const products = await Promise.all(handles.map((h) => fetch(`${base}/products/${h}.js`).then((r) => (r.ok ? r.json() : null)).catch(() => null)));
      const cards = products.filter(Boolean).map((p) => {
        const imgs = (p.images || []).slice(0, 3).map((src, i) => {
          const s = src.startsWith('//') ? 'https:' + src : src;
          const sized = s + (s.includes('?') ? '&' : '?') + 'width=600';
          return `<img class="tt__img${i === 0 ? ' is-active' : ''}" src="${sized}" width="600" height="900" loading="lazy" alt="${p.title.replace(/"/g, '&quot;')}">`;
        }).join('');
        const sale = p.compare_at_price && p.compare_at_price > p.price;
        return `<article class="card"><div class="card__media"><a href="${p.url}" class="card__link" tabindex="-1" aria-hidden="true"><turn-table class="tt">${imgs}</turn-table></a>${sale ? `<span class="badge">${T.strings.sale}</span>` : ''}</div><div class="card__info"><h3 class="card__title"><a href="${p.url}">${p.title}</a></h3><div class="price${sale ? ' price--sale' : ''}"><span class="price__now">${formatMoney(p.price)}</span>${sale ? `<s class="price__was">${formatMoney(p.compare_at_price)}</s>` : ''}</div></div></article>`;
      });
      if (!cards.length) { this.closest('.shopify-section').hidden = true; return; }
      track.innerHTML = cards.join('');
    }
  });

  /* ---------- Video banner play/pause ---------- */
  customElements.define('video-toggle', class extends HTMLElement {
    connectedCallback() {
      const video = this.closest('.shopify-section').querySelector('video');
      const btn = this.querySelector('button');
      if (!video || !btn) { this.hidden = true; return; }
      if (reduceMotion) { video.pause(); btn.setAttribute('aria-pressed', 'true'); }
      btn.addEventListener('click', () => {
        if (video.paused) { video.play(); btn.setAttribute('aria-pressed', 'false'); }
        else { video.pause(); btn.setAttribute('aria-pressed', 'true'); }
      });
    }
  });


  /* ---------- Marquees (announcement bar + text marquee) ----------
     Driven by the Web Animations API in pixels so they run on every phone: iOS Safari can leave pure-CSS
     marquees frozen after a bfcache restore, a tab switch, an orientation change or when a fixed bar
     resizes. CSS keyframes remain the fallback when JS hasn't run. */
  const initMarquees = (root = document) => {
    if (typeof Element === 'undefined' || !Element.prototype.animate) return;
    $$('.marquee', root).forEach((marquee) => {
      if (marquee._marquee) return;
      const tracks = $$('.marquee__track', marquee);
      if (!tracks.length) return;
      const state = { anims: [], width: 0 };
      marquee._marquee = state;
      const seconds = () => {
        const raw = getComputedStyle(marquee).getPropertyValue('--duration').trim() || getComputedStyle(tracks[0]).animationDuration;
        const n = parseFloat(raw);
        const s = Number.isFinite(n) && n > 0 ? (raw.endsWith('ms') ? n / 1000 : n) : 30;
        return reduceMotion ? s * 2 : s;
      };
      const start = () => {
        const width = tracks[0].getBoundingClientRect().width;
        if (!width) return;
        if (Math.abs(width - state.width) < 1 && state.anims.length && state.anims.every((a) => a.playState === 'running')) return;
        const progress = state.anims[0] && state.anims[0].effect ? (state.anims[0].effect.getComputedTiming().progress || 0) : 0;
        state.anims.forEach((a) => a.cancel());
        state.width = width;
        marquee.classList.add('marquee--js');
        state.anims = tracks.map((track) => {
          const anim = track.animate(
            [{ transform: 'translate3d(0, 0, 0)' }, { transform: `translate3d(${-width}px, 0, 0)` }],
            { duration: seconds() * 1000, iterations: Infinity, easing: 'linear' },
          );
          anim.currentTime = progress * seconds() * 1000;
          return anim;
        });
      };
      const resume = () => state.anims.forEach((a) => { if (a.playState !== 'running') a.play(); });
      start();
      if ('ResizeObserver' in window) new ResizeObserver(() => start()).observe(tracks[0]);
      window.addEventListener('orientationchange', () => setTimeout(start, 250));
      window.addEventListener('pageshow', () => { start(); resume(); });
      document.addEventListener('visibilitychange', () => { if (!document.hidden) resume(); });
      if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        marquee.addEventListener('mouseenter', () => state.anims.forEach((a) => a.pause()));
        marquee.addEventListener('mouseleave', resume);
      }
    });
  };

  /* ---------- Boot ---------- */
  const boot = () => {
    initLenis();
    onScroll(); setHeaderVars();
    if (lenis) lenis.on('scroll', runMotionTick);
    window.addEventListener('scroll', () => { onScroll(); if (!lenis) runMotionTick(); }, { passive: true });
    window.addEventListener('resize', runMotionTick);
    initParallax();
    runMotionTick();
    window.addEventListener('resize', debounce(setHeaderVars, 150));
    body.addEventListener('transitionend', debounce(setHeaderVars, 50));
    initCurtain();
    observeReveals();
    initAccordions();
    initMarquees();
    // keep header/cart count in sync when the page is restored from bfcache
    window.addEventListener('pageshow', (e) => { if (e.persisted) Cart.refreshCount(); });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();

  /* ---------- Theme editor support ---------- */
  document.addEventListener('shopify:section:load', (e) => { initMarquees(e.target); observeReveals(e.target); initAccordions(e.target); initCurtain(); initParallax(e.target); });
})();
